import React from "react";
import { useDispatch } from "react-redux";
import { Modal, Header, Button, Loader, Dimmer, Image, Grid } from "semantic-ui-react";
import StockTable from "../StockTable/StockTable";
import "./MissingSkuModal.css";
import { MissingStockItemMissingReason } from "../../utils/Constants";
import TimerDisplay from "../TimerDisplay/TimerDisplay";
import { setCurrentTotal } from "../../lib/store/reducers/missingSkuReducer";
import { IExtendedSortBoxItemModel } from "../../lib/SortBoxItem/IExtendedSortBoxItemModel";
import { IStockItem } from "../../lib/sku/IStockItem";

interface IProps {
  open: boolean;
  onCloseClearState: () => {};
  boxItem: IExtendedSortBoxItemModel;
  missingAmount: number;
  isLoadingShelves: boolean;
  reasonCode: string;
  reportMissingItem: (reasonCode: string, boxItem: IExtendedSortBoxItemModel) => {};
  missingButtonLoading: boolean;
  foundShelves: IStockItem[];
  missingSkuFound: (shelves: IStockItem[]) => {};
  currentTotal: { total: number; shelves: IStockItem[] };
  forceLiftShelfCallback: (shelfNumber: string) => {};
  cancelLiftShelfCallback: (shelfNumber: string) => {};
  forcedLiftShelves: string[];
}

const MissingSkuModalComponent = ({
  open,
  onCloseClearState,
  boxItem,
  missingAmount,
  isLoadingShelves,
  reasonCode,
  reportMissingItem,
  missingButtonLoading,
  foundShelves,
  missingSkuFound,
  currentTotal,
  forceLiftShelfCallback,
  cancelLiftShelfCallback,
  forcedLiftShelves,
}: IProps) => {
  const getMappedFoundShelves = (shelves: IStockItem[], boxItem: IExtendedSortBoxItemModel) => {
    if (shelves && shelves.length) {
      return shelves.map(
        (x: {
          name: string;
          shelfNumber: string;
          stockQuantity: number;
          isLiftShelf: boolean;
          isOriginalShelf?: boolean;
        }) => {
          const toReturn = { ...x };
          toReturn.isOriginalShelf =
            toReturn.shelfNumber === boxItem.shelfNumber ||
            (boxItem.shelfNumbers || []).includes(toReturn.shelfNumber);
          return toReturn;
        }
      );
    } else {
      return [];
    }
  };

  const dispatch = useDispatch();

  const setCurrentTotalFunc = (newTotal: { total: number; shelves: IStockItem[] }) => {
    dispatch(setCurrentTotal(newTotal));
  };

  return (
    <Modal
      open={open}
      onClose={onCloseClearState}
      className="MissingSkuModal"
      closeOnDimmerClick={false}
      closeOnDocumentClick={false}
      closeOnEscape={false}
    >
      <Header>
        <Header.Content className="skuModalContent">
          <Grid>
            <Grid.Column width={4}></Grid.Column>
            <Grid.Column width={8}>
              <h2>{"Artikel saknas"}</h2>
            </Grid.Column>
            <Grid.Column className="timerColumn" width={4}>
              <TimerDisplay timer="missingskutimer" type={3} trigger={boxItem.skuID} />
            </Grid.Column>
          </Grid>
          <div className={"ArticleInfo"}>
            <div className="ArticleInfoText">
              <div className="ArticleInfoTextName">
                <b>{boxItem.name}</b>
              </div>
              {(boxItem.rxIsNarc) ? (<div>
                <span style={{color: "red", float:"left", marginTop: "100px", position:"absolute", zIndex: 3}}>
                  <b style={{ fontSize: "1.5em" }}>Nark-vara!</b>
                </span>
              </div>) :null}
              <div className="ArticleInfoTextName">
                SkuID:
                <span style={{ marginLeft: "10px" }}>{boxItem.skuID}</span>
              </div>
              <div className="ArticleInfoTextName">
                {reasonCode === MissingStockItemMissingReason.Defective ? "Defekt" : "Saknat"}{" "}
                antal:
                <b style={{ marginLeft: "10px" }} className="MissingAmount">
                  {missingAmount}
                </b>
              </div>
              {boxItem.pickedBy > 0 && (
                <div className="PickedByText">
                  Plockad av <b>{boxItem.pickedByName}</b>
                </div>
              )}
            </div>
            <div className="ImageWrapper">
              <Image
                size="tiny"
                src={boxItem.imageURL}
                onError={(e: any) => (e.currentTarget.src = "https://placehold.co/280x280")}
              />
            </div>
          </div>
        </Header.Content>
      </Header>
      <Modal.Content>
        <Dimmer active={isLoadingShelves} inverted>
          <Loader />
        </Dimmer>
        {reasonCode === MissingStockItemMissingReason.Unknown ? (
          <div>
            <div className="Subtitle">Välj anledning</div>
            <div className="ButtonWrapper">
              <Button
                color="yellow"
                onClick={() => {
                  reportMissingItem(MissingStockItemMissingReason.Missing, boxItem);
                }}
                loading={missingButtonLoading}
              >
                Saknad vara
              </Button>
              <Button
                color="red"
                onClick={() => {
                  reportMissingItem(MissingStockItemMissingReason.Defective, boxItem);
                }}
                loading={missingButtonLoading}
              >
                Defekt vara
              </Button>
            </div>
          </div>
        ) : (
          <div>
            {!isLoadingShelves && foundShelves.length === 0 ? (
              <>
                <div className="Subtitle">Slut i lager</div>
                <div className="ButtonWrapper">
                  <Button
                    color="red"
                    onClick={() => missingSkuFound([])}
                    loading={missingButtonLoading}
                  >
                    STRYK ARTIKEL
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="Subtitle">Finns på följande platser:</div>
                <StockTable
                  shelves={getMappedFoundShelves(foundShelves, boxItem)}
                  missingAmount={missingAmount}
                  confirmCallback={missingSkuFound}
                  currentTotal={currentTotal}
                  setCurrentTotal={setCurrentTotalFunc}
                  forceLiftShelfCallback={forceLiftShelfCallback}
                  cancelLiftShelfCallback={cancelLiftShelfCallback}
                  forcedLiftShelves={forcedLiftShelves}
                />
                <div className="ButtonWrapper">
                  <Button
                    color="red"
                    className={missingAmount === 1 ? "" : "hidden"}
                    onClick={() => missingSkuFound([])}
                    loading={missingButtonLoading}
                  >
                    HITTAS EJ
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button secondary onClick={onCloseClearState} floated={"left"}>
          AVBRYT
        </Button>
        <Button
          primary
          className={missingAmount === 1 || foundShelves.length === 0 ? "hidden" : ""}
          disabled={reasonCode === MissingStockItemMissingReason.Unknown}
          loading={missingButtonLoading}
          onClick={() => missingSkuFound(currentTotal.shelves)}
        >
          BEKRÄFTA
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default MissingSkuModalComponent;
